import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Landmark, Upload, FileCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, imgUrl } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FIELDS = [
  ["holder", "Account holder name *"], ["account", "Bank account number *"],
  ["ifsc", "IFSC *"], ["bank_name", "Bank name"],
  ["pan", "PAN *"], ["gstin", "GSTIN *"],
];
const DOCS = [["cheque", "Cancelled cheque"], ["gst_certificate", "GST certificate"], ["pan_card", "PAN card"]];

export default function KYC() {
  const qc = useQueryClient();
  const [form, setForm] = useState({});
  const refs = { cheque: useRef(), gst_certificate: useRef(), pan_card: useRef() };

  const { data } = useQuery({ queryKey: ["kyc"], queryFn: async () => (await api.get("/agent/kyc")).data });
  useEffect(() => {
    if (data) {
      const k = data.kyc || {};
      setForm({
        holder: k.bank?.holder || "", account: k.bank?.account || "", ifsc: k.bank?.ifsc || "",
        bank_name: k.bank?.bank_name || "", pan: k.pan || "", gstin: k.gstin || "", address: k.address || "",
      });
    }
  }, [data]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/agent/kyc", form)).data,
    onSuccess: () => { toast.success("KYC saved — submitted for admin review"); qc.invalidateQueries({ queryKey: ["kyc"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const upload = async (docType, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/agent/kyc/upload?doc_type=${docType}`, fd);
      toast.success("Document uploaded");
      qc.invalidateQueries({ queryKey: ["kyc"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const status = data?.status || "incomplete";
  const docs = data?.kyc?.documents || {};

  return (
    <div data-testid="kyc-page" className="mx-auto max-w-2xl">
      <PageHeader title="KYC & Bank Details" sub="Required for withdrawals and GST invoicing. Verified by the IndiaFast team." testid="kyc-title">
        <StatusBadge status={status} />
      </PageHeader>

      {status === "rejected" && data?.kyc?.review_note && (
        <p data-testid="kyc-reject-note" className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-600">
          Rejected: {data.kyc.review_note} — please correct and resubmit.
        </p>
      )}
      {status === "verified" && (
        <p data-testid="kyc-verified-note" className="mb-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-700">
          <ShieldCheck size={14} /> Your KYC is verified — withdrawals are enabled.
        </p>
      )}

      <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-sm font-bold text-[#0F2440]"><Landmark size={15} className="text-[#1D5BD8]" /> Bank & tax details</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FIELDS.map(([k, label]) => (
            <div key={k}>
              <Label className="text-xs font-medium text-slate-500">{label}</Label>
              <Input data-testid={`kyc-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="mt-1.5 border-slate-200 bg-white text-[#0F2440]" />
            </div>
          ))}
          <div className="sm:col-span-2">
            <Label className="text-xs font-medium text-slate-500">Business address</Label>
            <Input data-testid="kyc-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="mt-1.5 border-slate-200 bg-white text-[#0F2440]" />
          </div>
        </div>
        <Button data-testid="kyc-save-btn" onClick={() => save.mutate()} disabled={save.isPending}
          className="mt-5 w-full bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">
          {save.isPending ? "Saving…" : "Save & submit KYC"}
        </Button>
      </div>

      <div className="mt-5 rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-sm font-bold text-[#0F2440]"><FileCheck size={15} className="text-[#1D5BD8]" /> Documents</h3>
        <div className="space-y-3">
          {DOCS.map(([k, label]) => (
            <div key={k} className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3">
              <div>
                <p className="text-xs font-semibold text-[#0F2440]">{label}</p>
                {docs[k] ? (
                  <a data-testid={`kyc-doc-view-${k}`} href={imgUrl(docs[k])} target="_blank" rel="noreferrer" className="text-[10px] font-medium text-[#1D5BD8] hover:underline">
                    View uploaded document
                  </a>
                ) : <p className="text-[10px] text-slate-400">Not uploaded</p>}
              </div>
              <Button data-testid={`kyc-upload-${k}`} size="sm" variant="outline" onClick={() => refs[k].current.click()}
                className="border-slate-200 text-slate-600 hover:bg-slate-50">
                <Upload size={12} /> {docs[k] ? "Re-upload" : "Upload"}
              </Button>
              <input ref={refs[k]} type="file" accept="image/*,.pdf" className="hidden" data-testid={`kyc-file-${k}`}
                onChange={(e) => e.target.files[0] && upload(k, e.target.files[0])} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
